import { assert } from '../helpers/assert.helper';
import { createElement } from '../helpers/element.helper';
const ROUTER_PARAMETER_PREFIX = ':';
const defaultOptions = {
    mode: 'history',
    baseUrl: '/',
};
/**
 * Router class for managing navigation and routing in a web application.
 * It supports both hash and history modes for navigation.
 * It allows defining routes, navigating to them, and mounting components based on the current URL.
 */
export class Router {
    _routes = [];
    _mode;
    _baseUrl;
    _parametersRegexp = new RegExp(`${ROUTER_PARAMETER_PREFIX}\\w+`, 'g');
    _mount;
    _currentRoute;
    constructor(options) {
        this._mode = options?.mode ?? defaultOptions.mode;
        this._baseUrl = options?.baseUrl ?? defaultOptions.baseUrl;
        window.addEventListener(this.eventName, (evt) => {
            evt.preventDefault();
            this.onUrlChange();
        });
    }
    get config() {
        return {
            mode: this._mode,
            baseUrl: this._baseUrl,
        };
    }
    /**
     * Gets the current route.
     * @throws Will throw an error if no route is found
     * @returns The current route
     */
    get currentRoute() {
        assert(this._currentRoute, 'No route found');
        return this._currentRoute;
    }
    /**
     * Adds a new route to the router.
     * @param path The path of the route
     * @param componentSelector The selector of the component to mount for this route
     * @param name An optional name for the route
     * @example
     * router.addRoute('/home', 'home-component', 'home');
     * @returns this
     */
    addRoute(path, componentSelector, name) {
        const pathWithoutParameters = path.replace(this._parametersRegexp, '(.+)');
        this._routes.push({
            name,
            path,
            componentSelector,
            regexp: new RegExp(`^${pathWithoutParameters}$`),
        });
        return this;
    }
    /**
     *  Adds a group of routes defined in a RouteGroup.
     * This allows for organizing routes under a common prefix.
     * @param routeGroup The RouteGroup containing routes to add
     * @example
     * const group = RouteGroup.create('/api')
     *   .addRoute('/users', 'user-list')
     *   .addRoute('/users/:id', 'user-detail');
     * router.addGroup(group);
     * @returns this
     */
    addGroup(routeGroup) {
        routeGroup
            .getRoutes()
            .forEach(({ path, componentSelector, name }) => this.addRoute(path, componentSelector, name));
        return this;
    }
    /**
     * Mounts the router on a specific HTML element.
     * @param el The element to mount the router on
     */
    mountOn(el) {
        this._mount = el;
        this.onUrlChange();
    }
    /**
     * Navigates to a route by its name.
     * @param name The name of the route to navigate to
     * @param params The parameters to include with the route
     */
    gotoName(name, params) {
        const route = this.getRouteByName(name);
        assert(route, `No route found for name: ${name}`);
        this.gotoPath(this.formatPath(route.path, params));
    }
    /**
     * Navigates to a route by its path.
     * @param path The path of the route to navigate to
     */
    gotoPath(path) {
        const urlPath = this._mode === 'hash' ? `#${path}` : path;
        window.history.pushState({}, '', `${this.baseUrl}/${urlPath}`.replace(/\/{2,}/g, '/'));
        this.onUrlChange();
    }
    get baseUrl() {
        return this._baseUrl.endsWith('/')
            ? this._baseUrl.slice(0, -1)
            : this._baseUrl;
    }
    get mountPoint() {
        assert(this._mount, 'No mount point defined');
        return this._mount;
    }
    get eventName() {
        return this._mode === 'hash' ? 'hashchange' : 'popstate';
    }
    get currentUrlPath() {
        const formattedPath = window.location.pathname.startsWith(this.baseUrl)
            ? window.location.pathname.slice(this.baseUrl.length)
            : window.location.pathname;
        const path = this._mode === 'hash' ? window.location.hash.slice(1) : formattedPath;
        return path.startsWith('/') ? path : `/${path}`;
    }
    onUrlChange() {
        const urlPath = this.currentUrlPath;
        const matchedRoute = this.getRouteByPath(urlPath);
        assert(matchedRoute, `No route found for path: ${urlPath}`);
        this._currentRoute = matchedRoute;
        const component = this.getComponentToLoad({ ...matchedRoute, urlPath });
        this.mountPoint.replaceChildren(component);
    }
    formatPath(routePath, params = {}) {
        return this.extractPathParams(routePath).reduce((acc, param) => {
            assert(params.hasOwnProperty(param), `Missing parameter "${param}" for path: ${routePath}`);
            return acc.replace(`${ROUTER_PARAMETER_PREFIX}${param}`, params[param]);
        }, routePath);
    }
    getComponentToLoad(matchedRoute) {
        const { componentSelector } = matchedRoute;
        const params = this.getRouteParameters(matchedRoute);
        return createElement(componentSelector, params);
    }
    getRouteParameters(matchedRoute) {
        const { urlPath, regexp, path } = matchedRoute;
        const match = regexp.exec(urlPath);
        assert(match, `No match found for path: ${urlPath}`);
        return this.extractPathParams(path).reduce((acc, param, index) => {
            return { ...acc, [param]: match[index + 1] };
        }, {});
    }
    getRouteByName(name) {
        return (this._routes.find(({ name: routeName }) => routeName === name) ?? null);
    }
    getRouteByPath(path) {
        return this._routes.find(({ regexp }) => regexp.test(path)) ?? null;
    }
    extractPathParams(path) {
        return path.split('/').reduce((acc, part) => {
            return part.startsWith(ROUTER_PARAMETER_PREFIX)
                ? [...acc, part.slice(ROUTER_PARAMETER_PREFIX.length)]
                : acc;
        }, []);
    }
}
